import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Stack,
} from "@mui/material";
import {
    ArrowBackIos as BackIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Description as DocumentsIcon,
    CalendarMonth as ScheduleIcon,
    People as ContactsIcon,
    InsertDriveFile as FileIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import QuoteService from "../services/Quote.service";

function LabelValue({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function FlagChip({ label }) {
    return <Chip label={label} size="small" color="primary" />;
}

function SubcontractorQuoteDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState("info");

    useEffect(() => {
        setLoading(true);
        setError("");
        QuoteService.get(id)
            .then((res) => setQuote(res.data))
            .catch(() => setError("Failed to load quote details."))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (panel) => (_, isOpen) => setExpanded(isOpen ? panel : false);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !quote) {
        return (
            <Box>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Alert severity="error">{error || "Quote not found."}</Alert>
            </Box>
        );
    }

    const documents = quote.documents || [];
    const attachments = quote.attachments_detail || [];
    const schedule = quote.schedule || [];
    const contacts = quote.contacts || [];
    const managerName = quote.manager_detail
        ? `${quote.manager_detail.first_name || ""} ${quote.manager_detail.last_name || ""}`.trim()
        : null;

    return (
        <Box>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            {quote.number}
                        </Typography>
                        {quote.is_complete && <Chip label="Won" size="small" color="success" />}
                        {quote.is_archived && !quote.is_complete && (
                            <Chip label="Archived" size="small" />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {quote.name}
                    </Typography>
                </Box>
            </Box>

            {/* ── Quote Info ── */}
            <Accordion
                expanded={expanded === "info"}
                onChange={toggle("info")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Quote Information
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <LabelValue label="Category" value={quote.project_category_detail?.name} />
                    <LabelValue label="Type" value={quote.project_type_detail?.name} />
                    <LabelValue label="Point of Contact" value={managerName} />
                    {quote.customers_detail?.length > 0 && (
                        <LabelValue
                            label="Customer"
                            value={quote.customers_detail.map((c) => c.name).join(", ")}
                        />
                    )}
                    {quote.quote_due_date && (
                        <LabelValue label="Due Date" value={new Date(quote.quote_due_date).toLocaleDateString()} />
                    )}
                    {quote.expected_order_date && (
                        <LabelValue label="Expected Order Date" value={new Date(quote.expected_order_date).toLocaleDateString()} />
                    )}
                    {(quote.travel_job || quote.prevailing_rate || quote.union) && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 0.75 }}
                            >
                                Flags
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {quote.travel_job && <FlagChip label="Travel Job" />}
                                {quote.prevailing_rate && <FlagChip label="Prevailing Rate" />}
                                {quote.union && <FlagChip label="Union" />}
                            </Stack>
                        </Box>
                    )}
                    {quote.notes && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 0.5 }}
                            >
                                Notes
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {quote.notes}
                            </Typography>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Documents ── */}
            <Accordion
                expanded={expanded === "documents"}
                onChange={toggle("documents")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DocumentsIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Documents
                        </Typography>
                        {documents.length + attachments.length > 0 && (
                            <Chip
                                label={documents.length + attachments.length}
                                size="small"
                                sx={{ height: 18, fontSize: 11 }}
                            />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {documents.length === 0 && attachments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No documents available for this quote.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {documents.map((doc, idx) => (
                                <React.Fragment key={`drawing-${doc.id}`}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItem
                                        secondaryAction={
                                            doc.current_document_url && (
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={doc.current_document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                        sx={{ py: 1 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FileIcon fontSize="small" color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={`${doc.sheet_number ? doc.sheet_number + " — " : ""}${doc.title}`}
                                            secondary={
                                                doc.current_revision
                                                    ? `Rev ${doc.current_revision}${doc.drawing_type_detail?.name ? ` • ${doc.drawing_type_detail.name}` : ""}`
                                                    : doc.drawing_type_detail?.name
                                            }
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                    </ListItem>
                                </React.Fragment>
                            ))}
                            {attachments.map((att, idx) => (
                                <React.Fragment key={`attachment-${att.id}`}>
                                    {(documents.length > 0 || idx > 0) && <Divider component="li" />}
                                    <ListItem
                                        secondaryAction={
                                            att.document_url && (
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={att.document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                        sx={{ py: 1 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FileIcon fontSize="small" color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={att.title}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                                        />
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Schedule ── */}
            <Accordion
                expanded={expanded === "schedule"}
                onChange={toggle("schedule")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ScheduleIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Schedule
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {schedule.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No schedule set for this quote yet.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {schedule.map((s, idx) => (
                                <React.Fragment key={s.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItem sx={{ py: 1 }}>
                                        <ListItemText
                                            primary={`${new Date(s.start_date).toLocaleDateString()} – ${new Date(s.end_date).toLocaleDateString()}`}
                                            secondary={s.notes || (s.is_scheduled ? "Scheduled" : "Tentative")}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                        {s.is_scheduled && (
                                            <Chip label="Confirmed" size="small" color="success" variant="outlined" />
                                        )}
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Contacts ── */}
            <Accordion
                expanded={expanded === "contacts"}
                onChange={toggle("contacts")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ContactsIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Contacts
                        </Typography>
                        {contacts.length > 0 && (
                            <Chip label={contacts.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {contacts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No contacts linked to this quote.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {contacts.map((c, idx) => (
                                <React.Fragment key={c.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        component={c.email ? "a" : "div"}
                                        href={c.email ? `mailto:${c.email}` : undefined}
                                        sx={{ py: 1 }}
                                    >
                                        <ListItemText
                                            primary={c.name}
                                            secondary={
                                                [c.job_title, c.company?.name, c.email, c.phone?.[0]?.phone_number]
                                                    .filter(Boolean)
                                                    .join(" • ")
                                            }
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export default SubcontractorQuoteDetail;
